import axios from "axios";

const OMDB_API_URL = "https://www.omdbapi.com/";
const OMDB_API_KEY =
    process.env.REACT_APP_OMDB_API_KEY;

// OMDb représente fréquemment une donnée absente par "N/A".
const normalizeOmdbValue = (value) => {
    if (
        value === undefined ||
        value === null ||
        value === "" ||
        value === "N/A"
    ) {
        return null;
    }

    return value;
};

const findOmdbRating = (ratings, source) => {
    if (!Array.isArray(ratings)) {
        return null;
    }

    const rating = ratings.find(
        (candidate) => candidate.Source === source
    );

    return normalizeOmdbValue(rating?.Value);
};

const buildOmdbRating = (value) => {
    const normalizedValue =
        normalizeOmdbValue(value);

    if (!normalizedValue) {
        return null;
    }

    const fractionMatch =
        normalizedValue.match(
            /^(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)$/
        );

    const percentageMatch =
        normalizedValue.match(
            /^(\d+(?:\.\d+)?)\s*%$/
        );

    let score = null;

    if (fractionMatch) {
        const receivedValue =
            Number(fractionMatch[1]);

        const maximumValue =
            Number(fractionMatch[2]);

        if (
            Number.isFinite(receivedValue) &&
            Number.isFinite(maximumValue) &&
            maximumValue > 0
        ) {
            score =
                (receivedValue / maximumValue) * 100;
        }
    } else if (percentageMatch) {
        score = Number(percentageMatch[1]);
    }

    if (!Number.isFinite(score)) {
        return null;
    }

    return {
        value: normalizedValue,

        score: Number(
            Math.min(
                100,
                Math.max(0, score)
            ).toFixed(1)
        ),
    };
};

export const fetchOmdbMovieData = async ({
    imdbId,
    title,
    year,
}) => {
    if (!OMDB_API_KEY) {
        console.warn(
            "[MovieFrenzy OMDb] La variable REACT_APP_OMDB_API_KEY est absente."
        );

        return null;
    }

    if (!imdbId && !title) {
        return null;
    }

    const params = {
        apikey: OMDB_API_KEY,
        type: "movie",
        plot: "short",
        r: "json",
    };

    /*
     * L’identifiant IMDb constitue la correspondance la plus fiable.
     * Le titre et l’année servent uniquement lorsqu’il est absent.
     */
    if (imdbId) {
        params.i = imdbId;
    } else {
        params.t = title;

        if (year) {
            params.y = year;
        }
    }

    try {
        const response = await axios.get(
            OMDB_API_URL,
            { params }
        );

        const omdbMovie = response.data;

        if (
            !omdbMovie ||
            omdbMovie.Response === "False"
        ) {
            console.info(
                "[MovieFrenzy OMDb] Aucun résultat correspondant.",
                omdbMovie?.Error || ""
            );

            return null;
        }

        return {
            imdbId:
                normalizeOmdbValue(omdbMovie.imdbID),

            title:
                normalizeOmdbValue(omdbMovie.Title),

            year:
                normalizeOmdbValue(omdbMovie.Year),

            ratings: {
                imdb: buildOmdbRating(
                    findOmdbRating(
                        omdbMovie.Ratings,
                        "Internet Movie Database"
                    )
                ),

                rottenTomatoes: buildOmdbRating(
                    findOmdbRating(
                        omdbMovie.Ratings,
                        "Rotten Tomatoes"
                    )
                ),

                metacritic: buildOmdbRating(
                    findOmdbRating(
                        omdbMovie.Ratings,
                        "Metacritic"
                    )
                ),
            },

            boxOffice:
                normalizeOmdbValue(omdbMovie.BoxOffice),

            awards:
                normalizeOmdbValue(omdbMovie.Awards),

            metascore:
                normalizeOmdbValue(omdbMovie.Metascore),

            imdbVotes:
                normalizeOmdbValue(omdbMovie.imdbVotes),
        };
    } catch (error) {
        console.warn(
            "[MovieFrenzy OMDb] Échec de la requête :",
            error
        );

        return null;
    }
};